/**
 * What every running agent is changing, arranged so an overlap is visible
 * before anybody reads a word.
 *
 * **Nothing here is a live clash.** Every agent works in its own worktree, so
 * two of them in one file break nothing at all — right up until one merges and
 * it lands on the other, hours after the decision that caused it. What this
 * shows is where two branches are heading for the same file, while it is still
 * a conversation about who owns it rather than a conflict somebody unpicks.
 *
 * A flat list of claims answers "who holds what" and hides the only question
 * that matters, which is "where do two of us meet". `web/src/room/Workspace.tsx`
 * and `web/src/room/` overlap, look nothing alike, and sorted alphabetically are
 * not even adjacent. A tree puts them one row apart.
 *
 * Pure functions only, like `status.ts` beside it: the tree, the overlap rule
 * and the collapse are the three things worth getting exactly right, and they
 * are testable without rendering anything.
 */
import type { Claim } from '../api/types'

export interface ClaimNode {
  /**
   * What this row shows. Usually one path segment, but a run of directories
   * nobody claimed and nothing branches in collapses into one — `web/src/room`
   * rather than three rows of scaffolding. This is what makes a real
   * repository's tree readable instead of a staircase.
   */
  label: string
  /** The whole path from the repository root, which is what a claim names. */
  path: string
  children: ClaimNode[]
  /** Claims made on exactly this path, in the order they were made. */
  holders: Claim[]
  /** Every distinct agent holding at or under here, sorted. */
  agents: string[]
  /**
   * Whether *this* claim is one of the two halves of an overlap.
   *
   * The overlap is reported on the shallower path — one agent holds
   * `web/src/claims`, another holds `web/src/claims/useClaims.ts`, and the row
   * that shouts is the directory. Without this the second half is drawn exactly
   * like an uninvolved file two rows away, and the reader has to work out which
   * of the children is the other party.
   *
   * Deliberately narrower than "an overlap is somewhere under here". Tinting
   * every ancestor implicates `web/src/room/SearchBar.tsx` for being in the
   * same directory tree as an argument it has nothing to do with, and there is
   * one accent in this interface precisely so it never means "nearby".
   */
  implicated: boolean
  /**
   * Whether two agents' claims actually overlap **here**.
   *
   * This is not "more than one agent is somewhere underneath". `web/src` with
   * one agent in `web/src/claims` and another in `web/src/room` is two agents
   * under one directory and no overlap at all: neither claimed `web/src`, and
   * the two paths do not overlap. Marking it would light up the scaffolding of
   * every busy repository and teach a reader to ignore the colour.
   *
   * A claim collides with another exactly when one contains the other, so the
   * test is: something is claimed *at* this node, and somebody else is holding
   * at or under it. Every descendant claim is inside this one by construction.
   */
  overlapping: boolean
  /** How deep to indent it. The root's children are 0. */
  depth: number
}

/** Whether two claimed paths are the same work. The rule, mirrored from
 *  `domain.paths_overlap` on the Python side — one definition per language and
 *  no third one hiding in a component. */
export function pathsOverlap(left: string, right: string): boolean {
  if (left === right) return true
  return left.startsWith(`${right}/`) || right.startsWith(`${left}/`)
}

interface Building {
  segment: string
  path: string
  children: Map<string, Building>
  holders: Claim[]
}

function blank(segment: string, path: string): Building {
  return { segment, path, children: new Map(), holders: [] }
}

/**
 * The claims as a tree, collapsed, with every node knowing who is under it.
 *
 * Sorted at every level including the top — see `byShape` — because a tree that
 * reorders itself as claims arrive is a tree nobody can keep their place in.
 */
export function claimTree(claims: Claim[]): ClaimNode[] {
  const root = blank('', '')
  for (const claim of claims) {
    let node = root
    const segments = claim.path.split('/').filter(Boolean)
    for (const segment of segments) {
      const path = node.path ? `${node.path}/${segment}` : segment
      let next = node.children.get(segment)
      if (!next) {
        next = blank(segment, path)
        node.children.set(segment, next)
      }
      node = next
    }
    node.holders.push(claim)
  }
  // Sorted here as well as inside `finish`, which only ever sees a node's
  // children: without this the top level came out in whatever order the claims
  // happened to arrive, and the one level everybody reads first was the one
  // level that reordered itself.
  const tree = [...root.children.values()]
    .map((child) => finish(child, child.segment, 0))
    .sort(byShape)
  markImplicated(tree, new Set())
  return tree
}

/**
 * Mark the deeper half of each overlap, walking down.
 *
 * A claim overlaps an ancestor's claim by definition — every path under
 * `web/src/claims` is inside it — so a node with holders is implicated the
 * moment any ancestor is held by somebody else. Only claims count: passing
 * through a directory nobody claimed carries nothing down.
 */
function markImplicated(nodes: ClaimNode[], above: Set<string>): void {
  for (const node of nodes) {
    const mine = new Set(node.holders.map((claim) => claim.agent))
    if (mine.size > 0 && [...above].some((agent) => !mine.has(agent))) {
      node.implicated = true
    }
    markImplicated(node.children, new Set([...above, ...mine]))
  }
}

function finish(node: Building, label: string, depth: number): ClaimNode {
  // Collapse a directory that holds nothing and branches nowhere into its only
  // child. Done before the children are finished so a run of them collapses in
  // one pass rather than leaving a staircase of single rows.
  if (node.holders.length === 0 && node.children.size === 1) {
    const only = [...node.children.values()][0]
    return finish(only, `${label}/${only.segment}`, depth)
  }

  const children = [...node.children.values()]
    .map((child) => finish(child, child.segment, depth + 1))
    .sort(byShape)

  const agents = [
    ...new Set([
      ...node.holders.map((claim) => claim.agent),
      ...children.flatMap((child) => child.agents),
    ]),
  ].sort()

  // Something is claimed here, and it is not all one agent's. Every claim below
  // this node is inside this node's claim, so that is the whole test — and it
  // is the same rule `domain.paths_overlap` applies pairwise on the server,
  // which is what keeps the tree and the overlap events agreeing.
  const overlapping = node.holders.length > 0 && agents.length > 1
  // Filled in by `markImplicated`, which needs to see ancestors and so cannot
  // run on the way up.
  return {
    label, path: node.path, children, holders: node.holders, agents,
    overlapping, implicated: overlapping, depth,
  }
}

/**
 * Rows that branch first, then alphabetically by label.
 *
 * "Branches" rather than "is a directory", because collapsing has already made
 * those different things: a lone `server/app.py` is one row with no children,
 * and it sorts among the leaves under the whole label it shows. Sorting it as a
 * directory would put it above files whose names come first, which is not what
 * the row says.
 */
function byShape(left: ClaimNode, right: ClaimNode): number {
  const leftIsBranch = left.children.length > 0
  const rightIsBranch = right.children.length > 0
  if (leftIsBranch !== rightIsBranch) return leftIsBranch ? -1 : 1
  return left.label.localeCompare(right.label)
}

/** Every overlap in the tree, in reading order. */
export function overlaps(nodes: ClaimNode[]): ClaimNode[] {
  return nodes.flatMap((node) => [
    ...(node.overlapping ? [node] : []),
    ...overlaps(node.children),
  ])
}

/**
 * What the header button says at rest.
 *
 * The same shape as `buttonState` for the status button, and the same rule: the
 * accent dot means something needs a person, never decoration. An overlap is the
 * one thing here that does, because two branches cannot decide between
 * themselves which of them owns a file.
 *
 * "2 overlaps" rather than "2 conflicts": nothing has conflicted yet, and a
 * button that cries conflict at a room where every branch still merges cleanly
 * is a button people stop reading.
 */
export function claimsButtonState(claims: Claim[]): { urgent: boolean; note: string | null } {
  if (claims.length === 0) return { urgent: false, note: null }
  const heading = overlaps(claimTree(claims)).length
  if (heading > 0) {
    return { urgent: true, note: heading === 1 ? '1 overlap' : `${heading} overlaps` }
  }
  const agents = new Set(claims.map((claim) => claim.agent)).size
  return { urgent: false, note: `${claims.length} held · ${agents} agent${agents === 1 ? '' : 's'}` }
}
